import { withBuildingDisplayHeight } from '@popety-io/utils'
import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import ShadeMap from 'mapbox-gl-shadow-simulator'
import SunCalc from 'suncalc'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'
import { API_BASE_URL, SHADEMAP_API_KEY } from '../../../../../config/constants'
import { useSunDirectionLayer } from './useSunDirectionLayer'
import {
  createBoundingBoxFromGeoJSON,
  createBoundingBoxFromCenter,
} from '../../Map.bbox'

interface UseSunlightLayersProps {
  map: React.RefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.RefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

const SHADOW_CONTEXT_BUILDINGS_LAYER = 'shadow-context-buildings'

// Global reference to ShadeMap instance
let shadeMapInstance: ShadeMap | null = null

// Global reference to mousemove handler for cleanup
let mouseMoveHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null

/**
 * Tear down the active ShadeMap instance.
 *
 * ShadeMap is a custom Mapbox layer whose `.remove()` calls
 * `map.removeLayer(this.id)`. Once a style swap has begun (e.g. switching to
 * satellite via `map.setStyle()`), Mapbox's internal `this.style` is briefly
 * `undefined`, so `removeLayer` throws
 * "Cannot read properties of undefined (reading 'removeLayer')". That left the
 * old code with a zombie instance whose WebGL render loop kept running and
 * white-paged the sunlight section.
 *
 * Two safeguards:
 * 1. The module ref is cleared FIRST, so a throwing teardown can never leave a
 *    dangling instance behind to be reused or re-removed.
 * 2. Callers must invoke this while the current style is still valid (i.e.
 *    BEFORE `map.setStyle()`), so `remove()` succeeds and the render loop stops.
 */
export const removeShadeMapInstance = () => {
  const instance = shadeMapInstance
  shadeMapInstance = null
  if (!instance) return
  try {
    instance.remove()
  } catch (error) {
    console.warn('[ShadeMap] Error removing instance:', error)
  }
}

/**
 * Collect all 3D building features for ShadeMap shadow casting.
 * Returns GeoJSON features with height properties from:
 * 1. building-footprints-source (existing buildings from useBuildingLayers)
 * 2. 3d-polygon-*-source (custom drawn polygons from MapDraw)
 */
const collectBuildingFeatures = async (
  map: mapboxgl.Map
): Promise<mapboxgl.MapboxGeoJSONFeature[]> => {
  const features: mapboxgl.MapboxGeoJSONFeature[] = []

  try {
    // 1. Get building footprints from the app's building GeoJSON source
    const buildingSource = map.getSource('building-footprints-source')
    if (buildingSource && buildingSource.type === 'geojson') {
      // Only include if the 3D extrusion layer exists (buildings are in 3D mode)
      const building3DLayer = map.getLayer('building-3d-layer')
      if (building3DLayer) {
        const sourceData = (buildingSource as any)._data
        if (sourceData && typeof sourceData === 'object') {
          const data = sourceData as any
          const featureList =
            data.type === 'FeatureCollection'
              ? data.features
              : data.type === 'Feature'
                ? [data]
                : []

          for (const feature of featureList) {
            if (
              feature.geometry?.type === 'Polygon' ||
              feature.geometry?.type === 'MultiPolygon'
            ) {
              const height = feature.properties?.height || 10
              features.push({
                ...feature,
                properties: {
                  ...feature.properties,
                  height,
                },
              } as mapboxgl.MapboxGeoJSONFeature)
            }
          }
        }
      }
    }

    // 2. Get custom drawn polygons from MapDraw (3d-polygon-*-source)
    const style = map.getStyle()
    if (style?.layers) {
      for (const layer of style.layers) {
        if (
          layer.id.startsWith('3d-polygon-') &&
          layer.type === 'fill-extrusion'
        ) {
          const sourceId = `${layer.id}-source`
          const source = map.getSource(sourceId)
          if (source && source.type === 'geojson') {
            const sourceData = (source as any)._data
            if (sourceData && typeof sourceData === 'object') {
              const data = sourceData as any
              const featureList =
                data.type === 'FeatureCollection'
                  ? data.features
                  : data.type === 'Feature'
                    ? [data]
                    : []

              for (const feature of featureList) {
                if (
                  feature.geometry?.type === 'Polygon' ||
                  feature.geometry?.type === 'MultiPolygon'
                ) {
                  features.push({
                    ...feature,
                    properties: {
                      ...feature.properties,
                      height: feature.properties?.height || 10,
                    },
                  } as mapboxgl.MapboxGeoJSONFeature)
                }
              }
            }
          }
        }
      }
    }
  } catch (error) {
    console.warn('[ShadeMap] Error collecting building features:', error)
  }

  return features
}

const SHADOW_CONTEXT_SOURCE = 'shadow-context-buildings-source'

/**
 * Compute a bounding box for nearby buildings query.
 * Prefers buildingGeoJson bounds (expanded), falls back to propertyCoordinates.
 * Returns { top_left, bottom_right } format for the API.
 */
const computeShadowContextBounds = (
  buildingGeoJson: any,
  propertyCoordinates?: [number, number]
): {
  top_left: { lat: number; lon: number }
  bottom_right: { lat: number; lon: number }
} | null => {
  let bbox: mapboxgl.LngLatBoundsLike | null = null

  // Use property coordinates as center with a 1000m radius
  if (propertyCoordinates) {
    bbox = createBoundingBoxFromCenter(propertyCoordinates, 1000)
  } else if (buildingGeoJson) {
    // Fallback: derive center from buildingGeoJson bounds
    const geoBbox = createBoundingBoxFromGeoJSON(buildingGeoJson)
    if (geoBbox) {
      const geoBounds = geoBbox as [[number, number], [number, number]]
      const center: [number, number] = [
        (geoBounds[0][0] + geoBounds[1][0]) / 2,
        (geoBounds[0][1] + geoBounds[1][1]) / 2,
      ]
      bbox = createBoundingBoxFromCenter(center, 500)
    }
  }

  if (!bbox) return null

  const bounds = Array.isArray(bbox)
    ? (bbox as [[number, number], [number, number]])
    : null
  if (!bounds) return null

  const [sw, ne] = bounds

  return {
    top_left: { lat: ne[1], lon: sw[0] },
    bottom_right: { lat: sw[1], lon: ne[0] },
  }
}

/**
 * Build the vector tile URL for nearby buildings with bbox filter.
 * Excludes specific building IDs via the API's exclude_building_ids filter.
 */
const buildShadowContextTileUrl = (
  bounds: {
    top_left: { lat: number; lon: number }
    bottom_right: { lat: number; lon: number }
  },
  excludeBuildingIds?: string[]
): string => {
  const query = JSON.stringify({
    filters: {
      bounds,
      ...(excludeBuildingIds?.length
        ? { exclude_building_ids: excludeBuildingIds }
        : {}),
    },
    fields: [
      'height_estimate_to_display',
      'height_estimate',
      'height',
      'floor_nb',
    ],
  })

  return (
    `${API_BASE_URL}/buildings/vector-tile/geo_polygon_wkt/{z}/{x}/{y}` +
    `?query=${encodeURIComponent(query)}`
  )
}

/**
 * Add vector tile source and fill-extrusion layer for nearby white buildings.
 * Uses the buildings vector-tile API endpoint (same as Explore map).
 */
const addShadowContextBuildingsLayer = (
  map: mapboxgl.Map,
  bounds: {
    top_left: { lat: number; lon: number }
    bottom_right: { lat: number; lon: number }
  },
  excludeBuildingIds?: string[]
) => {
  const tileUrl = buildShadowContextTileUrl(bounds, excludeBuildingIds)

  if (!map.getSource(SHADOW_CONTEXT_SOURCE)) {
    map.addSource(SHADOW_CONTEXT_SOURCE, {
      type: 'vector',
      tiles: [tileUrl],
      minzoom: 13,
      maxzoom: 16,
    })
  }

  if (!map.getLayer(SHADOW_CONTEXT_BUILDINGS_LAYER)) {
    map.addLayer({
      id: SHADOW_CONTEXT_BUILDINGS_LAYER,
      type: 'fill-extrusion',
      source: SHADOW_CONTEXT_SOURCE,
      'source-layer': 'hits',
      paint: {
        'fill-extrusion-color': '#FFFFFF',
        'fill-extrusion-opacity': 0.9,
        'fill-extrusion-height': withBuildingDisplayHeight([
          'coalesce',
          ['get', 'height_estimate'],
          ['get', 'height'],
          ['*', ['get', 'floor_nb'], 3],
          10,
        ]) as mapboxgl.ExpressionSpecification,
        'fill-extrusion-base': 0,
      },
    })

    // Move below any existing building-3d-layer to avoid z-fighting
    if (map.getLayer('building-3d-layer')) {
      map.moveLayer(SHADOW_CONTEXT_BUILDINGS_LAYER, 'building-3d-layer')
    }
  }
}

const removeShadowContextBuildingsLayer = (map: mapboxgl.Map) => {
  if (map.getLayer(SHADOW_CONTEXT_BUILDINGS_LAYER)) {
    map.removeLayer(SHADOW_CONTEXT_BUILDINGS_LAYER)
  }
  if (map.getSource(SHADOW_CONTEXT_SOURCE)) {
    map.removeSource(SHADOW_CONTEXT_SOURCE)
  }
}

/**
 * Hook to handle sunlight study layers (shadow simulation and sunlight hours)
 * Uses mapbox-gl-shadow-simulator library with Swiss terrain data
 */
export const useSunlightLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseSunlightLayersProps) => {
  // Handle sun direction indicator layer
  useSunDirectionLayer({
    map,
    mapLoaded,
    styleLoadedCount,
    otherMapOptions,
  })

  // Handle white context buildings layer for shadow visualization
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const activeSection = (otherMapOptions as any).activeSection
    const isSunlightSection =
      activeSection === 'sunlight' || activeSection === 'sunlight_study'
    const layerStates = (otherMapOptions as any).demographicsLayerStates
    const buildingGeoJson = (otherMapOptions as any).buildingGeoJson
    const propertyCoordinates = (otherMapOptions as any).propertyCoordinates
    const excludeBuildingIds = (otherMapOptions as any).excludeBuildingIds as
      string[] | undefined

    const contextLayerId = SHADOW_CONTEXT_BUILDINGS_LAYER
    const contextLayerConfig = getLayerConfig(contextLayerId)
    const contextEnabled =
      layerStates?.[contextLayerId]?.enabled ??
      contextLayerConfig?.defaultEnabled !== false

    const shadowLayerId = 'sunlight-shadow-simulation'
    const sunlightHoursLayerId = 'sunlight-hours'

    const shadowLayerConfig = getLayerConfig(shadowLayerId)
    const sunlightHoursConfig = getLayerConfig(sunlightHoursLayerId)

    const shadowEnabled =
      layerStates?.[shadowLayerId]?.enabled ??
      shadowLayerConfig?.defaultEnabled !== false
    const sunlightHoursEnabled =
      layerStates?.[sunlightHoursLayerId]?.enabled ??
      sunlightHoursConfig?.defaultEnabled !== false

    const simulationActive =
      shadowEnabled || (!shadowEnabled && sunlightHoursEnabled)
    const shouldShow = isSunlightSection && simulationActive && contextEnabled

    // Add/remove the source + layer. Wrapped so a transient Mapbox error can
    // never escape and white-page the sunlight section.
    const applyContextLayer = () => {
      if (!map.current) return
      try {
        if (shouldShow) {
          const bounds = computeShadowContextBounds(
            buildingGeoJson,
            propertyCoordinates
          )
          if (bounds) {
            addShadowContextBuildingsLayer(
              map.current,
              bounds,
              excludeBuildingIds
            )
          }
        } else {
          removeShadowContextBuildingsLayer(map.current)
        }
      } catch (error) {
        console.warn('[ShadeMap] Error updating shadow context layer:', error)
      }
    }

    // After a style swap (e.g. toggling satellite) this effect re-runs before
    // Mapbox has finished loading the new style, so addSource/addLayer throw
    // "Style is not done loading". Guard with isStyleLoaded() and defer to the
    // next 'idle' event when the style isn't ready yet.
    let pendingIdleHandler: (() => void) | null = null
    if (map.current.isStyleLoaded()) {
      applyContextLayer()
    } else {
      pendingIdleHandler = () => {
        pendingIdleHandler = null
        applyContextLayer()
      }
      map.current.once('idle', pendingIdleHandler)
    }

    return () => {
      if (pendingIdleHandler && map.current) {
        map.current.off('idle', pendingIdleHandler)
      }
      if (map.current) {
        try {
          removeShadowContextBuildingsLayer(map.current)
        } catch {
          // Style may be mid-swap; the layer will be gone with the old style.
        }
      }
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.activeSection,
    (otherMapOptions as any).buildingGeoJson,
    (otherMapOptions as any).propertyCoordinates,
    JSON.stringify((otherMapOptions as any).excludeBuildingIds),
    JSON.stringify(
      otherMapOptions.demographicsLayerStates?.[SHADOW_CONTEXT_BUILDINGS_LAYER]
    ),
    JSON.stringify(
      otherMapOptions.demographicsLayerStates?.['sunlight-shadow-simulation']
    ),
    JSON.stringify(otherMapOptions.demographicsLayerStates?.['sunlight-hours']),
  ])

  // Handle shadow simulation and sunlight hours layers - initialization and mode changes
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    const activeSection = (otherMapOptions as any).activeSection
    const isSunlightSection =
      activeSection === 'sunlight' || activeSection === 'sunlight_study'
    const layerStates = (otherMapOptions as any).demographicsLayerStates
    const sunlightConfig = (otherMapOptions as any).sectionData?.sunlightConfig

    const shadowLayerId = 'sunlight-shadow-simulation'
    const sunlightHoursLayerId = 'sunlight-hours'

    // Get layer states
    const shadowLayerConfig = getLayerConfig(shadowLayerId)
    const sunlightHoursLayerConfig = getLayerConfig(sunlightHoursLayerId)

    const shadowLayerState = layerStates?.[shadowLayerId] || {
      enabled: shadowLayerConfig?.defaultEnabled !== false,
      opacity: shadowLayerConfig?.defaultOpacity || 70,
    }

    const sunlightHoursLayerState = layerStates?.[sunlightHoursLayerId] || {
      enabled: sunlightHoursLayerConfig?.defaultEnabled !== false,
      opacity: sunlightHoursLayerConfig?.defaultOpacity || 70,
    }

    // Determine which mode is active (exclusive group - only one can be enabled)
    const isShadowMode = shadowLayerState.enabled
    const isSunlightHoursMode = !isShadowMode && sunlightHoursLayerState.enabled

    // Determine if we should show ShadeMap
    const shouldShowShadeMap =
      isSunlightSection && (isShadowMode || isSunlightHoursMode)

    // Helper function to set up sunlight hours tooltip
    const setupSunlightHoursTooltip = () => {
      const mapInstance = map.current
      if (!mapInstance || !shadeMapInstance) return

      // Remove existing handler if any
      if (mouseMoveHandler) {
        mapInstance.off('mousemove', mouseMoveHandler)
      }

      // Create new handler
      mouseMoveHandler = (e: mapboxgl.MapMouseEvent) => {
        if (!shadeMapInstance) return

        const { point } = e
        const hours = shadeMapInstance.getHoursOfSun(point.x, point.y)

        if (hours !== null && hours !== undefined) {
          const hoursRounded = Math.round(hours * 10) / 10 // Round to 1 decimal

          if (!popup.current) {
            popup.current = new mapboxgl.Popup({
              closeButton: false,
              closeOnClick: false,
              className: 'sunlight-hours-popup',
            })
          }

          popup.current
            .setLngLat(e.lngLat)
            .setHTML(
              `<div style="padding: 4px 8px;"><strong>${hoursRounded} hours</strong> of sun</div>`
            )
            .addTo(mapInstance)
        }
      }

      // Add handler to map
      mapInstance.on('mousemove', mouseMoveHandler)
    }

    // Helper function to remove sunlight hours tooltip
    const removeSunlightHoursTooltip = () => {
      if (mouseMoveHandler && map.current) {
        map.current.off('mousemove', mouseMoveHandler)
        mouseMoveHandler = null
      }
      if (popup.current) {
        popup.current.remove()
      }
    }

    // Fallback guard: if a style swap starts without ShadeMap having been torn
    // down first (the primary teardown happens in Map.tsx before setStyle),
    // remove it here. removeShadeMapInstance() clears the ref before calling
    // remove(), so a throw mid-swap can't leave a zombie render loop behind
    // (Fixes POPETY-IO-APP-V2-1D and the satellite white-page regression).
    const handleStyleLoading = () => {
      removeShadeMapInstance()
    }
    map.current.on('styledataloading', handleStyleLoading)

    // Only create/update ShadeMap if we should show it
    if (shouldShowShadeMap) {
      // Get map center coordinates for sunrise/sunset calculation
      const center = map.current.getCenter()
      const currentDate = sunlightConfig?.date || new Date()

      // Calculate sunrise and sunset times for the current date and location
      const sunTimes = SunCalc.getTimes(currentDate, center.lat, center.lng)

      // Create ShadeMap instance if it doesn't exist
      if (!shadeMapInstance) {
        try {
          // Get API key from environment
          const apiKey = SHADEMAP_API_KEY as string

          if (!apiKey) {
            console.error(
              '[ShadeMap] VITE_SHADEMAP_API_KEY environment variable is not set. Shadow visualization will not work.'
            )
            return
          }

          // Swiss terrain configuration
          // Use proxy in development to bypass CORS, direct URL in production
          const shadeMapBaseUrl =
            import.meta.env.DEV && window.location.hostname === 'localhost'
              ? '/shademap-tiles'
              : 'https://cfw.shademap.app'

          const terrainSource = {
            maxZoom: 16,
            tileSize: 512,
            getSourceUrl: ({ x, y, z }: { x: number; y: number; z: number }) =>
              `${shadeMapBaseUrl}/switzerland/${z}/${x}/${y}.webp`,
            getElevation: ({ r, g }: { r: number; g: number }) =>
              ((r * 256 + g) / 2 ** 16) * 4808,
            _overzoom: 18,
          }

          // Determine initial opacity based on which mode is active
          const initialOpacity = isShadowMode
            ? shadowLayerState.opacity / 100
            : sunlightHoursLayerState.opacity / 100

          // Create ShadeMap instance with all required options
          const mapInstance = map.current
          shadeMapInstance = new ShadeMap({
            map: mapInstance,
            apiKey,
            date: currentDate,
            terrainSource,
            color: '#000000',
            opacity: initialOpacity,
            getFeatures: () => collectBuildingFeatures(mapInstance),
          })

          // Add to map
          shadeMapInstance.addTo(map.current)

          // Enable sun exposure mode if in sunlight hours mode
          if (isSunlightHoursMode) {
            shadeMapInstance.setSunExposure(true, {
              startDate: sunTimes.sunrise,
              endDate: sunTimes.sunset,
              iterations: 32, // Number of samples for calculation
            } as any)

            // Set up tooltip to show hours of sun on hover
            // Wait a bit for the sun exposure calculation to complete
            setTimeout(() => {
              setupSunlightHoursTooltip()
            }, 100)
          }
        } catch (error) {
          console.error('Error creating ShadeMap instance:', error)
          return
        }
      } else {
        // Update existing ShadeMap instance - handle mode changes
        try {
          // Update opacity based on which mode is active
          const opacity = isShadowMode
            ? shadowLayerState.opacity / 100
            : sunlightHoursLayerState.opacity / 100

          shadeMapInstance.setOpacity(opacity)

          // Toggle between shadow mode and sun exposure mode
          if (isSunlightHoursMode) {
            // Enable sun exposure mode with sunrise/sunset for the current date

            shadeMapInstance.setSunExposure(true, {
              startDate: sunTimes.sunrise,
              endDate: sunTimes.sunset,
              iterations: 32,
            } as any)

            // Set up tooltip to show hours of sun on hover
            setTimeout(() => {
              setupSunlightHoursTooltip()
            }, 100)
          } else if (isShadowMode) {
            // Disable sun exposure mode (return to shadow mode)
            shadeMapInstance.setSunExposure(false)

            // Remove tooltip handler
            removeSunlightHoursTooltip()
          }
        } catch (error) {
          console.warn('Error updating ShadeMap:', error)
        }
      }
    }

    // Cleanup function - Only cleanup when fundamentals change (style, section, mode)
    // NOT when just the date/time changes - that's handled by the separate effect below
    return () => {
      // Remove style loading guard
      if (map.current) {
        map.current.off('styledataloading', handleStyleLoading)
      }

      // Remove mousemove handler if it exists
      if (mouseMoveHandler && map.current) {
        map.current.off('mousemove', mouseMoveHandler)
        mouseMoveHandler = null
      }

      // Remove popup if it exists
      if (popup.current) {
        popup.current.remove()
      }

      // Remove ShadeMap instance
      removeShadeMapInstance()
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.activeSection,
    // Include layer states to detect mode switching - but NOT date changes
    JSON.stringify(
      otherMapOptions.demographicsLayerStates?.['sunlight-shadow-simulation']
    ),
    JSON.stringify(otherMapOptions.demographicsLayerStates?.['sunlight-hours']),
  ])

  // Separate effect for handling date/time updates - this runs without cleanup
  // This allows smooth real-time updates without destroying/recreating the ShadeMap instance
  useEffect(() => {
    if (!map.current || !mapLoaded || !shadeMapInstance) {
      return
    }

    const sunlightConfig = (otherMapOptions as any).sectionData?.sunlightConfig
    const currentDate = sunlightConfig?.date || new Date()

    try {
      // Update date only - this is a lightweight operation that doesn't require full recreation
      shadeMapInstance.setDate(currentDate)
    } catch (error) {
      console.warn('[ShadeMap] Error updating date:', error)
    }

    // NO cleanup - we want the ShadeMap instance to persist
  }, [
    mapLoaded,
    // CRITICAL: Use .getTime() to convert Date to number for proper change detection
    // Date objects are compared by reference, not value, so we need a primitive
    otherMapOptions.sectionData?.sunlightConfig?.date?.getTime(),
  ])
}
